import { connect } from 'react-redux';
import { get, groupBy, keyBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
// MUI
import { Box } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';

// Commons
import { ADMIN_FEATURE_PATHS } from '@commons/utils/featurePaths';
import {
  AdminSubPageContentContainer,
  GenericContentContainer,
  GenericPageContainer,
  TimeSlotsHeaderActionContainer,
  TimeSlotsHeaderContainer,
} from '@commons/Layout/styledComponents';
import { Button } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import Drawer from '@commons/Drawer';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { COMMON_DATAGRID_PROPS, DATAGRID_SLOTS } from './datagrid/datagridProps';
import { DATAGRID_LOCALES, handleCellClick, handleCellEditStop } from '@commons/utils/dataGrid';
import { StyledDataGridContainer } from './datagrid/styledComponents';

import { exportStoreTimeSlots } from './utils/export';
import { getActions } from './utils/actions';
import { getColumns } from './utils/columns';
import { getFormattedDataWithTimeSlots } from './utils/format';

// Selectors
import {
  canDuplicateTimeslots,
  canEditStoreTimeslots,
  canEditStoreTimeslotsAllocation,
} from '@selectors/actions/storeActions';
import { getAuthorizedActionsForPaths } from '@selectors/featureProps';

// Actions
import {
  closeGenericModal,
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';
import { loading, loadingSuccess } from '@actions/loading';
import { receiveStores } from '@actions/store';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Services
import { user as userService } from '@services/user';
import storeService from '@services/store';

import ProductTimeSlotsAllocationManagement from './ProductTimeSlotAllocationManagement';
import TimeSlotManagement from './TimeSlotManagement';

// Time Slots Duplication
import { ActionsContainer } from '@admin/stores/StoreDetailsModules/StoreTimeSlots/styledComponents';
import { getClientInfo } from '@selectors/client';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTimeSlotsConfirmationModal, getTimeSlotsSetupModal } from '../../utils/modalUtils';
import { MODAL_STEP as TIME_SLOTS_MODAL_STEP } from '../../components/StoreTimeSlotsSetupModal';
import TimeSlotsProgressBarModal from '../../components/StoreTimeSlotsSetupModal/progressBarModal';

const StoreTimeSlots = (props) => {
  const {
    storeParams,
    user,
    showErrorMessage,
    pageLoading,
    pageLoaded,
    history,
    authorizedActions,
    userRights,
    updateStoresInRedux,
    openGenericModal,
    refreshGenericModal,
    openModalExportInfo,
    stores,
    client: { hasMultipleBrands, storeName },
  } = props;

  const storeNameSingle = getClientStoreNameTranslation(storeName).toLowerCase();
  const storeNamePlural = getClientStoreNameTranslation(storeName, true);

  const apiRef = useGridApiRef();

  /*******************
   ***** STATES *****
   *******************/

  const [timeSlots, setTimeSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayTimeSlotsPanel, setDisplayTimeSlotsPanel] = useState(false);
  const [displayPTSAPanel, setDisplayPTSAPanel] = useState(false);
  const [actions, setActions] = useState([]);
  const [hasUserAccessToProductsAssortement, setHasUserAccessToProductsAssortement] =
    useState(false);

  // Datagrid pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Cell editing state for touch devices
  const [cellModesModel, setCellModesModel] = useState({});

  // Row animation state for border color changes
  const [rowAnimations, setRowAnimations] = useState({});

  // Datagrid contents
  const [storeProductMappings, setStoreProductMappings] = useState([]);
  const [products, setProducts] = useState([]);
  const [columns, setColumns] = useState([]);
  const [productsRowsWithTimeSlots, setProductsRowsWithTimeSlots] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  // Time Slots Duplication
  const [storeIdReference, setStoreIdReference] = useState(null);
  const [modalStep, setModalStep] = useState(null);

  /*******************
   ***** EFFECTS *****
   *******************/

  useEffect(() => {
    const hasAccess = userRights.some(
      (right) => right.path === ADMIN_FEATURE_PATHS.ADMIN_STORES_DETAILS_PRODUCTION_PLANNING,
    );

    setHasUserAccessToProductsAssortement(hasAccess);
  }, [userRights]);

  useEffect(() => {
    (async () => {
      await getProductionTimeSlotsWithProductAllocations();
    })();
  }, []);

  // Update rows when data changes (either after API fetch, or after user interaction)
  useEffect(() => {
    const formattedProductsDataWithTimeSlots = getFormattedDataWithTimeSlots(products);

    setProductsRowsWithTimeSlots(formattedProductsDataWithTimeSlots);
  }, [products]);

  // Update columns when rowAnimations change
  useEffect(() => {
    if (timeSlots.length > 0 && products.length > 0) {
      const canEditAllocation = canEditStoreTimeslotsAllocation(authorizedActions);

      setColumns(getColumns(timeSlots, products, rowAnimations, canEditAllocation));
    }
  }, [rowAnimations, timeSlots, products, authorizedActions]);

  useEffect(() => {
    const canEditAllocation = canEditStoreTimeslotsAllocation(authorizedActions);
    const canDuplicateStoreTimeslots = canDuplicateTimeslots(authorizedActions);

    setActions(
      getActions(
        selectedProductIds,
        openProductTimeSlotsAllocationPanel,
        handleXLSExport,
        selectedProductIds.length,
        canEditAllocation,
        canDuplicateStoreTimeslots,
        handleTimeSlotsSetup,
        timeSlots.length > 0,
      ),
    );
  }, [productsRowsWithTimeSlots, selectedProductIds, authorizedActions, timeSlots]);

  useEffect(() => {
    if (!modalStep) {
      return;
    }

    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    let modalConfig;

    const currentStore = [
      {
        id: storeParams.id,
        name: storeParams.name,
      },
    ];

    switch (modalStep) {
      case TIME_SLOTS_MODAL_STEP.STORE_SELECTION:
        modalConfig = getTimeSlotsSetupModal(
          stores,
          currentStore,
          storeNameSingle,
          hasMultipleBrands,
          storeIdReference,
          setStoreIdReference,
          resetModalStates,
          setModalStep,
        );
        break;

      case TIME_SLOTS_MODAL_STEP.VALIDATION:
        const timeSlotsReferenceStore = stores.find(({ id }) => id === storeIdReference);
        const timeSlotsReferenceStoreName = timeSlotsReferenceStore
          ? timeSlotsReferenceStore.name
          : '-';

        modalConfig = getTimeSlotsConfirmationModal(
          currentStore,
          timeSlotsReferenceStoreName,
          storeNameSingle,
          storeNamePlural,
          resetModalStates,
          setModalStep,
        );
        break;

      case TIME_SLOTS_MODAL_STEP.PROGRESS_BAR:
        handleTimeSlotsProgressBarModal(currentStore, storeIdReference);
        return;

      default:
        break;
    }

    if (isModalOpened && modalStep !== TIME_SLOTS_MODAL_STEP.VALIDATION) {
      refreshGenericModal(modalConfig);
      return;
    }

    openGenericModal(modalConfig);
  }, [stores, storeIdReference, modalStep]);

  /*******************
   ***** METHODS *****
   *******************/

  const getProductionTimeSlotsWithProductAllocations = async () => {
    try {
      pageLoading();
      setIsLoading(true);

      const timeSlots = await storeService.getValidStoreTimeSlots(storeParams.id);

      setTimeSlots(timeSlots.filter(({ name }) => name !== null));

      await getProductTimeSlotsAllocations(timeSlots);
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_LOADING_ERROR'));
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  const getProductTimeSlotsAllocations = async (timeSlots) => {
    try {
      const { id: storeId } = storeParams;

      const [storeProductMappingsResult, productTimeSlotsAllocations] = await Promise.all([
        storeService.getProductionProductsOfStore(storeId),
        storeService.getProductTimeSlotsAllocations(storeId),
      ]);

      setStoreProductMappings(storeProductMappingsResult);

      const PTSAGroupedByProductId = groupBy(productTimeSlotsAllocations, 'productId');

      const formattedProducts = storeProductMappingsResult.map((spm) => {
        const { product, launchDate, endDate } = spm;

        const productTimeSlotAllocations = get(PTSAGroupedByProductId, [product.id], []);
        const dataByTimeSlotIds = keyBy(productTimeSlotAllocations, 'productionTimeSlotId');

        return {
          id: product.id,
          name: get(product, 'name', ''),
          sku: get(product, 'sku', ''),
          category: get(product, 'category', null) || i18next.t('GENERAL.NONE_VALUE'),
          subCategory: get(product, 'subCategory', null) || i18next.t('GENERAL.NONE_VALUE'),
          launchDate: launchDate
            ? moment(launchDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
            : null,
          endDate: endDate
            ? moment(endDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
            : null,
          dataByTimeSlotIds,
        };
      });

      setProducts(formattedProducts);

      const canEditAllocation = canEditStoreTimeslotsAllocation(authorizedActions);
      setColumns(getColumns(timeSlots, formattedProducts, rowAnimations, canEditAllocation));
    } catch {
      showErrorMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATIONS_LOADING_ERROR'));
    }
  };

  const saveProductTimeSlotAllocations = async (productId, allocations) => {
    setIsLoading(true);

    try {
      await storeService.updateProductTimeSlotsAllocations(
        storeParams.id,
        [productId],
        allocations,
      );
    } catch (error) {
      showErrorMessage(i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATIONS_MANAGEMENT_ERROR'));
    } finally {
      setIsLoading(false);
    }
  };

  /*******************
   ***** HANDLERS *****
   *******************/

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const openAssociateTimeSlotsPanel = () => {
    setDisplayTimeSlotsPanel(true);
  };

  const openProductTimeSlotsAllocationPanel = () => {
    setDisplayPTSAPanel(true);
  };

  const handleSelectionModelChange = (newSelectionModel) => {
    setSelectedProductIds(newSelectionModel);
  };

  const handleCloseTimeSlotManagementPanel = async ({ needRefresh = false } = {}) => {
    setDisplayTimeSlotsPanel(false);

    if (needRefresh) {
      await getProductionTimeSlotsWithProductAllocations();

      await updateStoresInRedux();
    }
  };

  const handleCloseProductTimeSlotsAllocationPanel = async ({ needRefresh = false } = {}) => {
    setDisplayPTSAPanel(false);

    if (needRefresh) {
      await getProductionTimeSlotsWithProductAllocations();
    }
  };

  const updateAllocationRatio = (id, value, updatedRow, newTotal) => {
    // Convert to number and validate range
    const numericValue = parseFloat(value);

    // Enforce value of 0 if attempting to clear the field
    if (value === '' || value === null || value === undefined) {
      updatedRow[id] = 0;
      updatedRow.total = newTotal;
    }

    if (!isNaN(numericValue)) {
      updatedRow[id] = numericValue;
      updatedRow.total = newTotal;
    }
  };

  // Function to trigger row animation
  const triggerRowAnimation = (rowId, total) => {
    const animationType = total === 100 ? 'success' : 'error';

    setRowAnimations((prev) => ({
      ...prev,
      [rowId]: {
        isAnimating: true,
        animationType,
        total,
      },
    }));

    // Reset animation after 2 seconds
    setTimeout(() => {
      setRowAnimations((prev) => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          isAnimating: false,
        },
      }));
    }, 2000);
  };

  const handleProcessRowUpdate = async (updatedRow, originalRow) => {
    // Need to do a deep copy for the catch block to work properly
    const originalRowClone = { ...originalRow };

    const { id: productId } = updatedRow;

    const modifiedTimeSlotCell = timeSlots.find(({ id }) => updatedRow[id] !== originalRow[id]);

    if (!modifiedTimeSlotCell) {
      return originalRow;
    }

    const updatedRatio = updatedRow[modifiedTimeSlotCell.id];
    const oldRatio = originalRowClone[modifiedTimeSlotCell.id];

    const hasChanges = updatedRatio !== oldRatio;

    if (hasChanges) {
      // const delta = Math.round(updatedRatio - oldRatio);
      const timeSlotIds = timeSlots.map(({ id }) => id);
      let updatedRowTotal = 0;

      const productTimeSlotAllocations = timeSlotIds.map((timeSlotId) => {
        updatedRowTotal += updatedRow[timeSlotId] || 0;
        return {
          productionTimeSlotId: timeSlotId,
          ratio: updatedRow[timeSlotId],
        };
      });

      try {
        updateAllocationRatio(modifiedTimeSlotCell.id, updatedRatio, updatedRow, updatedRowTotal);

        // Trigger animation after successful update
        triggerRowAnimation(productId, updatedRowTotal);

        // Call saveProductTimeSlotAllocations only if the total is 100
        if (updatedRowTotal === 100) {
          await saveProductTimeSlotAllocations(productId, productTimeSlotAllocations);
        }

        // Even if the total is not 100, we still need to return the updated row to show the wrong ratio but we don't call API
        return updatedRow;
      } catch {
        return originalRowClone;
      }
    }

    return originalRowClone;
  };

  const handleXLSExport = () => {
    const selectedRows = apiRef.current.getSelectedRows();

    let productsToExport = productsRowsWithTimeSlots;

    if (selectedRows.size > 0) {
      productsToExport = Array.from(selectedRows.values());
    }

    exportStoreTimeSlots(productsToExport, timeSlots, storeParams.name);
  };

  const resetModalStates = () => {
    setStoreIdReference(null);
    setModalStep(null);
  };

  const handleTimeSlotsSetup = () => {
    const currentStore = [
      {
        id: storeParams.id,
        name: storeParams.name,
      },
    ];

    const timeSlotsSetupModalConfig = getTimeSlotsSetupModal(
      stores,
      currentStore,
      storeNameSingle,
      hasMultipleBrands,
      storeIdReference,
      setStoreIdReference,
      resetModalStates,
      setModalStep,
    );

    setModalStep(TIME_SLOTS_MODAL_STEP.STORE_SELECTION);

    openGenericModal(timeSlotsSetupModalConfig);
  };

  const handleTimeSlotsProgressBarModal = (stores, storeIdReference) => {
    openModalExportInfo({
      component: TimeSlotsProgressBarModal,
      customStyle: {
        overflow: 'initial',
      },
      stores,
      storeIdReference,
      setModalStep,
      closeModalCleanUp: async () => {
        await getProductionTimeSlotsWithProductAllocations();
        await updateStoresInRedux();
      },
    });
  };

  return (
    <GenericPageContainer>
      <AdminSubPageContentContainer>
        {!isLoading && !timeSlots.length && (
          <EmptyState
            icon={'/images/inpulse/production-planning.svg'}
            label={i18next.t('ADMIN.STORES.TIME_SLOTS_EMPTY_STATE_TITLE')}
            labelColor={ENUM_COLORS.IP_BLACK_1}
            labelFont={ENUM_FONTS.H2}
            renderActionButton={() => {
              const canDuplicateStoreTimeslots = canDuplicateTimeslots(authorizedActions);
              const canEditTimeslots = canEditStoreTimeslots(authorizedActions);

              if (!canDuplicateStoreTimeslots && !canEditTimeslots) {
                return null;
              }

              return (
                <ActionsContainer>
                  {canDuplicateStoreTimeslots && (
                    <Button
                      color={'inpulse-outline'}
                      handleClick={handleTimeSlotsSetup}
                      icon={'/images/inpulse/settings.svg'}
                      label={i18next.t('ADMIN.STORES.TIME_SLOTS_CONFIGURE_FROM_CLONE')}
                    />
                  )}
                  {canEditTimeslots && (
                    <Button
                      color={'inpulse-default'}
                      handleClick={openAssociateTimeSlotsPanel}
                      icon={'/images/inpulse/add-white-small.svg'}
                      label={i18next.t('GENERAL.ADD')}
                    />
                  )}
                </ActionsContainer>
              );
            }}
            subtitle={i18next.t('ADMIN.STORES.TIME_SLOTS_EMPTY_STATE_SUBTITLE')}
            subtitleMargin={'8px'}
          />
        )}

        {timeSlots.length && (
          <GenericContentContainer height="calc(100% - 12px)" overflow="hidden">
            <TimeSlotsHeaderContainer>
              <Box sx={{ paddingBottom: '24px' }}>
                <LeftRightSplitter
                  left={<Text>{i18next.t('ADMIN.STORES.TIME_SLOTS_ALLOCATIONS_TITLE')}</Text>}
                  right={
                    <TimeSlotsHeaderActionContainer>
                      <Button
                        actions={actions}
                        color={'inpulse-outline'}
                        iconDropdown={'/images/inpulse/carret-black.svg'}
                        iconOnLeft={false}
                        label={i18next.t('GENERAL.ACTIONS')}
                      />
                      {canEditStoreTimeslots(authorizedActions) && (
                        <Button
                          color={'inpulse-default'}
                          handleClick={openAssociateTimeSlotsPanel}
                          icon={'/images/inpulse/backoffice-white-small.svg'}
                          label={i18next.t('ADMIN.STORES.TIME_SLOTS_MANAGEMENT_ACTION')}
                          minWidth={200}
                        />
                      )}
                    </TimeSlotsHeaderActionContainer>
                  }
                  style={{ alignItems: 'flex-end' }}
                />
              </Box>
            </TimeSlotsHeaderContainer>

            <StyledDataGridContainer>
              <DataGridPro
                apiRef={apiRef}
                cellModesModel={cellModesModel}
                columns={columns}
                loading={isLoading}
                localeText={
                  DATAGRID_LOCALES[user.lnkLanguageAccountrel.code].components.MuiDataGrid
                    .defaultProps.localeText
                }
                paginationModel={paginationModel}
                rows={productsRowsWithTimeSlots}
                {...COMMON_DATAGRID_PROPS({ hasEmptyState: !storeProductMappings.length })}
                pageSizeOptions={[20, 50, 100, 200, 500]}
                processRowUpdate={handleProcessRowUpdate}
                slots={{
                  ...DATAGRID_SLOTS,
                  noRowsOverlay: () => (
                    <EmptyState
                      backgroundColor={'transparent'}
                      label={i18next.t('ADMIN.STORES.TIME_SLOTS_PRODUCTS_EMPTY_STATE_TITLE')}
                      labelColor={ENUM_COLORS.IP_BLACK_1}
                      labelFont={ENUM_FONTS.H2}
                      renderActionButton={() =>
                        hasUserAccessToProductsAssortement && (
                          <Button
                            color={'inpulse-default'}
                            handleClick={() => {
                              history.push(`/admin/stores/${storeParams.id}/products`);
                            }}
                            icon={'/images/inpulse/add-white-small.svg'}
                            label={i18next.t('GENERAL.ADD')}
                          />
                        )
                      }
                      subtitle={i18next.t('ADMIN.STORES.TIME_SLOTS_PRODUCTS_EMPTY_STATE_SUBTITLE')}
                      subtitleMargin={'8px'}
                    />
                  ),
                }}
                checkboxSelection
                checkboxSelectionVisibleOnly
                disableRowSelectionOnClick
                pagination
                onCellClick={(params, event) => {
                  handleCellClick(params, event, setCellModesModel);
                }}
                onCellEditStop={(params) => handleCellEditStop(params, setCellModesModel)}
                onCellModesModelChange={handleCellModesModelChange}
                onPaginationModelChange={setPaginationModel}
                onRowSelectionModelChange={handleSelectionModelChange}
              />
            </StyledDataGridContainer>
          </GenericContentContainer>
        )}

        <Drawer
          customHeight={'100%'}
          customWidth={'40%'}
          isOpened={displayTimeSlotsPanel}
          minWidth={'400px'}
          overlayOpacity={'none'}
          onClick={() => {
            setDisplayTimeSlotsPanel(false);
          }}
        >
          <TimeSlotManagement
            storeId={storeParams.id}
            storeName={storeParams.name}
            storeTimeSlots={timeSlots}
            onClose={handleCloseTimeSlotManagementPanel}
          />
        </Drawer>

        <Drawer
          customHeight={'100%'}
          customWidth={'40%'}
          isOpened={displayPTSAPanel}
          minWidth={'400px'}
          overlayOpacity={'none'}
          onClick={() => {
            setDisplayPTSAPanel(false);
          }}
        >
          <ProductTimeSlotsAllocationManagement
            selectedProductIds={selectedProductIds}
            storeId={storeParams.id}
            storeName={storeParams.name}
            storeTimeSlots={timeSlots}
            onClose={handleCloseProductTimeSlotsAllocationPanel}
          />
        </Drawer>
      </AdminSubPageContentContainer>
    </GenericPageContainer>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  authorizedActions: getAuthorizedActionsForPaths(state.baseReducer.userRights, [
    '/admin/stores',
    '/admin/stores/:id/time-slots',
  ]),
  userRights: state.baseReducer.userRights,
  stores: state.baseReducer.stores,
  client: getClientInfo(state.baseReducer.user),
  modal: state.modalReducer,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  updateStoresInRedux: async () => {
    const accountId = localStorage.getItem('userId');
    const stores = await userService.getStoresOfAccount(accountId);
    dispatch(receiveStores(stores));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  refreshGenericModal: (params, customPropertyPathsToUpdate) => {
    dispatch(refreshGenericModal(params, customPropertyPathsToUpdate));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(StoreTimeSlots);
